/**
 * lib/payslipPdf.ts
 * ------------------------------------------------------------------
 * HR PAYROLL — payslip PDF renderer (Phase 1, Day 6).
 *
 * Mirrors lib/reportPdf.ts (jsPDF, school crest/logo, SCHOOL config)
 * and reproduces the LAYOUT of the school's existing payslip without
 * reproducing its arithmetic error: our totals reconcile exactly
 * (net = gross − deductions), because they come from the assembled
 * payslip (lib/assemblePayslip).
 *
 * Every payslip carries the compliance disclaimer footer and records
 * (in the body) which tax-table version computed PAYE.
 *
 * buildPayslipPdf(p, opt).output('blob') → upload to Storage / email.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { fmtPula } from '@/lib/hrPayroll';
import { periodLabel, earningLines, deductionLines, type AssembledPayslip } from '@/lib/assemblePayslip';
import { LEAVE_TYPE_LABELS } from '@/lib/hr';

const LEAVE_TYPE_NA = '—';

const NAVY: [number, number, number] = [27, 58, 107];
const INK: [number, number, number] = [31, 41, 55];
const MUTED: [number, number, number] = [107, 114, 128];
const RULE: [number, number, number] = [209, 213, 219];

const A4_W = 210;
const A4_H = 297;
const MX = 16; // left/right margin

export interface PayslipPdfOptions {
  logoDataUrl?: string | null;
  /** Disclaimer template; {DATE} is replaced with the tax-table label. */
  disclaimer?: string;
}

const DEFAULT_DISCLAIMER =
  'PAYE calculated using Botswana tax tables effective {DATE}. The School is responsible for verifying the calculation against current BURS regulations. Acepsle Pty Ltd makes no representation as to the accuracy of statutory deductions.';

function setText(pdf: jsPDF, c: [number, number, number]) { pdf.setTextColor(c[0], c[1], c[2]); }
function setDraw(pdf: jsPDF, c: [number, number, number]) { pdf.setDrawColor(c[0], c[1], c[2]); }
function setFill(pdf: jsPDF, c: [number, number, number]) { pdf.setFillColor(c[0], c[1], c[2]); }

function crest(pdf: jsPDF, cx: number, cy: number, r: number, logo?: string | null) {
  if (logo) {
    try { pdf.addImage(logo, 'PNG', cx - r, cy - r, r * 2, r * 2); return; } catch { /* fall through */ }
  }
  setDraw(pdf, NAVY); pdf.setLineWidth(0.6); pdf.circle(cx, cy, r, 'S');
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(r * 0.8);
  pdf.text('LKC', cx, cy + r * 0.25, { align: 'center' });
}

function money(pdf: jsPDF, value: number, xRight: number, y: number, bold = false) {
  pdf.setFont('helvetica', bold ? 'bold' : 'normal');
  pdf.text(fmtPula(value), xRight, y, { align: 'right' });
}

export function renderPayslip(pdf: jsPDF, p: AssembledPayslip, opt: PayslipPdfOptions = {}): void {
  const cx = A4_W / 2;
  let y = 16;

  // ---- Letterhead ----
  crest(pdf, MX + 8, y + 4, 9, opt.logoDataUrl);
  setText(pdf, NAVY);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text(SCHOOL.name, cx, y, { align: 'center' });
  setText(pdf, MUTED);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text(SCHOOL.contact.addressLines.join(', '), cx, y + 5.5, { align: 'center' });
  pdf.text(`Tel: ${SCHOOL.contact.phonePrimary}`, cx, y + 10, { align: 'center' });
  y += 16;
  setDraw(pdf, NAVY); pdf.setLineWidth(0.5); pdf.line(MX, y, A4_W - MX, y);
  y += 8;

  // ---- Title ----
  setText(pdf, INK);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
  pdf.text('PAY SLIP', MX, y);
  setText(pdf, MUTED);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(`${p.staffName} · ${periodLabel(p.period)}`, A4_W - MX, y, { align: 'right' });
  y += 7;

  // ---- Employee info (two columns) ----
  const colL = MX, colR = cx + 6;
  const fromDate = new Date(p.period.from).toLocaleDateString();
  const toDate = new Date(p.period.to).toLocaleDateString();
  const left: [string, string][] = [
    ['Name', p.staffName],
    ['Designation', p.jobTitle],
    ['Department', p.department ?? '—'],
    ['Reference', p.reference],
  ];
  const right: [string, string][] = [
    ['Date From', fromDate],
    ['Date To', toDate],
    ['Employee No', p.employeeNo],
    ['Bank Account', p.bank?.accountNumber ?? '—'],
  ];
  pdf.setFontSize(8.5);
  const rows = Math.max(left.length, right.length);
  for (let i = 0; i < rows; i++) {
    const ry = y + i * 5;
    if (left[i]) infoCell(pdf, colL, ry, left[i][0], left[i][1]);
    if (right[i]) infoCell(pdf, colR, ry, right[i][0], right[i][1]);
  }
  y += rows * 5 + 4;

  // ---- Earnings table ----
  y = sectionTable(pdf, y, 'Earnings', earningLines(p).map((l) => [l.label, l.amount]), p.earningsTotal, 'Gross');

  // ---- Deductions table (with balance column) ----
  y = deductionsTable(pdf, y + 2, p);

  // ---- Net pay ----
  y += 3;
  setFill(pdf, [243, 246, 250]); setDraw(pdf, RULE); pdf.setLineWidth(0.3);
  pdf.roundedRect(MX, y, A4_W - MX * 2, 16, 2, 2, 'FD');
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('NET PAY', MX + 4, y + 7);
  pdf.setFontSize(13); setText(pdf, NAVY);
  pdf.text(`BWP ${fmtPula(p.net)}`, A4_W - MX - 4, y + 7, { align: 'right' });
  setText(pdf, MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8);
  pdf.text(`Amount in words: ${p.netInWords}`, MX + 4, y + 12.5);
  y += 22;

  // ---- Leave summary ----
  if (p.leaveBalances.length > 0) {
    y = leaveSummary(pdf, y, p);
  }

  // ---- Loan / scheme balances ----
  const balances = deductionLines(p).filter((l) => typeof l.remainingBalance === 'number');
  if (balances.length > 0) {
    setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    pdf.text('Outstanding balances', MX, y); y += 5;
    setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
    for (const b of balances) {
      pdf.text(b.label, MX, y);
      pdf.text(`Remaining: ${fmtPula(b.remainingBalance!)}`, A4_W - MX, y, { align: 'right' });
      y += 4.5;
    }
    y += 2;
  }

  // ---- Gratuity breakdown (final payslip) ----
  if (p.gratuity) {
    const g = p.gratuity;
    setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    pdf.text('Gratuity payout', MX, y); y += 5;
    setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
    const gr: [string, number][] = [
      ['Gross gratuity', g.grossGratuity],
      ['Tax-exempt (50%)', g.exemptAmount],
      [`Taxable (50%) @ ${g.marginalRatePct}% marginal`, g.taxableAmount],
      ['Gratuity tax', g.tax],
      ['Net gratuity', g.net],
    ];
    for (const [lbl, val] of gr) {
      pdf.text(lbl, MX, y);
      pdf.text(fmtPula(val), A4_W - MX, y, { align: 'right' });
      y += 4.5;
    }
    y += 2;
  }

  // ---- Footer: disclaimer + signature ----
  const footerY = A4_H - 26;
  setDraw(pdf, RULE); pdf.setLineWidth(0.3); pdf.line(MX, footerY, A4_W - MX, footerY);
  const disclaimer = (opt.disclaimer ?? DEFAULT_DISCLAIMER).replace('{DATE}', p.taxTableLabel);
  setText(pdf, MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(6.8);
  const lines = pdf.splitTextToSize(disclaimer, A4_W - MX * 2);
  pdf.text(lines, MX, footerY + 4);
  setText(pdf, INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  pdf.text('Authorised signature: ______________________', MX, A4_H - 8);
  pdf.text('Company stamp', A4_W - MX, A4_H - 8, { align: 'right' });
}

function infoCell(pdf: jsPDF, x: number, y: number, label: string, value: string) {
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
  pdf.text(label.toUpperCase(), x, y);
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  pdf.text(value || '—', x + 26, y, { maxWidth: 62 });
}

function sectionTable(
  pdf: jsPDF, y: number, title: string, rows: [string, number][], total: number, totalLabel: string,
): number {
  const xR = A4_W - MX;
  // header band
  setFill(pdf, NAVY); pdf.rect(MX, y, A4_W - MX * 2, 7, 'F');
  setText(pdf, [255, 255, 255]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  pdf.text(title.toUpperCase(), MX + 3, y + 4.8);
  pdf.text('AMOUNT (BWP)', xR - 3, y + 4.8, { align: 'right' });
  y += 7;
  setText(pdf, INK); pdf.setFontSize(8.5);
  for (const [label, amount] of rows) {
    y += 5.5;
    pdf.setFont('helvetica', 'normal');
    pdf.text(label, MX + 3, y);
    money(pdf, amount, xR - 3, y);
  }
  y += 2;
  setDraw(pdf, RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, A4_W - MX, y);
  y += 5;
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text(totalLabel.toUpperCase(), MX + 3, y);
  money(pdf, total, xR - 3, y, true);
  return y + 3;
}

function deductionsTable(pdf: jsPDF, y: number, p: AssembledPayslip): number {
  const xR = A4_W - MX;
  const rows = deductionLines(p);
  setFill(pdf, NAVY); pdf.rect(MX, y, A4_W - MX * 2, 7, 'F');
  setText(pdf, [255, 255, 255]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  pdf.text('DEDUCTIONS', MX + 3, y + 4.8);
  pdf.text('AMOUNT (BWP)', xR - 3, y + 4.8, { align: 'right' });
  y += 7;
  setText(pdf, INK); pdf.setFontSize(8.5);
  for (const l of rows) {
    y += 5.5;
    pdf.setFont('helvetica', 'normal');
    pdf.text(l.label, MX + 3, y);
    if (typeof l.remainingBalance === 'number') {
      setText(pdf, MUTED); pdf.setFontSize(7);
      pdf.text(`bal ${fmtPula(l.remainingBalance)}`, xR - 30, y, { align: 'right' });
      setText(pdf, INK); pdf.setFontSize(8.5);
    }
    money(pdf, l.amount, xR - 3, y);
  }
  y += 2;
  setDraw(pdf, RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, A4_W - MX, y);
  y += 5;
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text('TOTAL DEDUCTIONS', MX + 3, y);
  money(pdf, p.deductionsTotal, xR - 3, y, true);
  return y + 3;
}

function leaveSummary(pdf: jsPDF, y: number, p: AssembledPayslip): number {
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text('Leave summary', MX, y); y += 5;
  // header
  const c1 = MX, c2 = MX + 60, c3 = MX + 95, c4 = MX + 130;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7);
  /* The row is a statement of the MONTH: what was carried in, what was used,
     what is left. Labelling the first column "entitled" was what made it look
     wrong — it held a full-year allowance beside a part-year balance. The
     brought-forward figure is already net of every earlier month's leave,
     which is what the accrual accounts for. */
  pdf.text('TYPE', c1, y); pdf.text('BROUGHT FORWARD', c2, y); pdf.text('TAKEN THIS MONTH', c3, y); pdf.text('BALANCE', c4, y);
  y += 1; setDraw(pdf, RULE); pdf.setLineWidth(0.2); pdf.line(MX, y, A4_W - MX, y); y += 4;
  setText(pdf, INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  for (const b of p.leaveBalances) {
    const label = LEAVE_TYPE_LABELS[b.type as keyof typeof LEAVE_TYPE_LABELS] ?? b.type;
    pdf.text(label, c1, y);
    pdf.text(b.entitlement > 0 ? `${b.entitlement} d` : LEAVE_TYPE_NA, c2, y);
    pdf.text(`${b.taken} d`, c3, y);
    pdf.text(`${b.remaining} d`, c4, y);
    y += 4.5;
  }
  return y + 3;
}

export function buildPayslipPdf(p: AssembledPayslip, opt: PayslipPdfOptions = {}): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  renderPayslip(pdf, p, opt);
  return pdf;
}
